import React, { useState, useEffect } from 'react';
import api from '../../../api';
import './BlogsSection.css';

const GRADIENTS = [
  'linear-gradient(135deg, #5356e6 0%, #7b7def 60%, #a5a7f8 100%)',
  'linear-gradient(135deg, #f0c755 0%, #e8a020 60%, #c9a02e 100%)',
  'linear-gradient(135deg, #10b981 0%, #059669 60%, #047857 100%)',
  'linear-gradient(135deg, #ec4899 0%, #db2777 60%, #be185d 100%)'
];

const COLORS = [
  '#5356e6',
  '#c9a02e',
  '#059669',
  '#db2777'
];

export default function BlogsSection({ onSeeAllClick }) {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const res = await api.get('/blogs.php');
        const data = Array.isArray(res.data) ? res.data : [];
        
        // Sort newest published first
        data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        
        // Only keep top 4 blogs (2x2 grid)
        setBlogs(data.slice(0, 4));
      } catch (err) {
        console.error("Failed to load public blogs:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  const getDomain = (urlStr) => {
    try {
      const url = new URL(urlStr);
      return url.hostname.replace('www.', '');
    } catch (e) {
      return 'External Source';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <section id="blogs" className="blogs">
      {/* Background decoration */}
      <div className="blogs__bg-pattern" />

      <div className="blogs__inner">
        {/* ── Header ── */}
        <div className="blogs__header">
          <div className="blogs__eyebrow">
            <i className="fa-solid fa-rss"></i>
            <span>Student Support</span>
          </div>
          <h2 className="blogs__title">
            Curated <span className="blogs__title-accent">Blogs</span> &amp; Articles
          </h2>
          <p className="blogs__subtitle">
            Handpicked articles from trusted sources — expanding your horizon beyond
            the classroom and the library shelves.
          </p>
        </div>

        {/* ── Cards Grid ── */}
        <div className="blogs__grid">
          {loading ? (
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
              Loading articles...
            </div>
          ) : blogs.length === 0 ? (
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)', fontWeight: 600 }}>
              No curated articles available at the moment.
            </div>
          ) : (
            blogs.map((blog, i) => {
              const gradient = GRADIENTS[i % 4];
              const categoryColor = COLORS[i % 4];
              const domain = getDomain(blog.link_url);
              const displayIcon = blog.icon || 'fa-solid fa-book-open';

              return (
                <article
                  className="blog-card"
                  key={blog.id || i}
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  {/* Thumbnail */}
                  <div
                    className="blog-card__thumb"
                    style={{ background: gradient }}
                  >
                    <div className="blog-card__thumb-icon">
                      <i className={displayIcon}></i>
                    </div>
                    {/* External link corner badge */}
                    <div className="blog-card__ext-badge">
                      <i className="fa-solid fa-arrow-up-right-from-square"></i>
                    </div>
                    {/* Category chip on image */}
                    <div
                      className="blog-card__cat-chip"
                      style={{ background: `rgba(255,255,255,0.18)` }}
                    >
                      {domain}
                    </div>
                  </div>

                  {/* Body */}
                  <div className="blog-card__body">
                    {/* Meta */}
                    <div className="blog-card__meta">
                      <span className="blog-card__source">
                        <i className="fa-solid fa-globe"></i>
                        {domain}
                      </span>
                      <span className="blog-card__read-time">
                        <i className="fa-regular fa-calendar"></i>
                        {formatDate(blog.created_at)}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="blog-card__title" title={blog.title}>{blog.title}</h3>

                    {/* Excerpt */}
                    <p className="blog-card__excerpt" title={blog.description}>
                      {blog.description || 'No summary available.'}
                    </p>

                    {/* CTA */}
                    <a
                      href={blog.link_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="blog-card__cta"
                      style={{ '--blog-color': categoryColor }}
                    >
                      Read Article
                      <i className="fa-solid fa-arrow-up-right-from-square"></i>
                    </a>
                  </div>
                </article>
              );
            })
          )}
        </div>

        {/* ── View All & Notes ── */}
        <div className="blogs__cta-container">
          <div className="blogs__cta">
            <a 
              href="#blogs" 
              className="blogs__view-all"
              onClick={(e) => {
                e.preventDefault();
                onSeeAllClick?.();
              }}
            >
              <i className="fa-solid fa-list"></i>
              Browse Articles Archive
            </a>
          </div>
          <p className="blogs__cta-note">
            <i className="fa-solid fa-circle-info"></i>
            These articles are hosted on external websites and will open in a new tab.
          </p>
        </div>
      </div>
    </section>
  );
}
